import {
  NativeStackHeaderProps,
  Stack,
  useFocusEffect,
  useRouter,
} from 'expo-router';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

import AutoScrollPillGroup from './AutoScrollPillGroup';
import CategoryContent from './CategoryContent';
import {
  BrowseByCategoryDataResult,
  useBrowseByCategoryData,
} from './hooks/useBrowseByCategoryData';
import useScrollPersistence from './hooks/useScrollPersistence';

const BrowseByCategoryOffers = () => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'browseByCategory',
  });
  const { defaultTitle, defaultMessage, doneButtonTitle } =
    useDefaultErrorStrings();

  const browseData: BrowseByCategoryDataResult = useBrowseByCategoryData();
  const {
    listRef,
    handleScroll,
    markRestorePending,
    handleContentSizeChange,
    resetScroll,
  } = useScrollPersistence();

  const {
    location,
    pillConfigs,
    selectedCategoryId,
    selectedCategoryName,
    isLoading,
    hasError,
    sections,
    offersLoading,
    refetch,
    hidePills,
  } = browseData;

  useFocusEffect(
    useCallback(() => {
      refetch();
      markRestorePending();
    }, [refetch, markRestorePending]),
  );

  const handleCategorySelect = (config: { id: string; labelText: string }) => {
    if (config.id !== selectedCategoryId) {
      resetScroll();
      router.setParams({
        category: config.id,
        name: config.labelText,
      });

      listRef.current?.scrollToOffset({ offset: 0, animated: false });
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: insets.bottom }]}>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      {pillConfigs.length > 0 && selectedCategoryId && !hidePills ? (
        <AutoScrollPillGroup
          pillConfigs={pillConfigs}
          onSelection={handleCategorySelect}
          selectedPillId={selectedCategoryId}
        />
      ) : null}

      {selectedCategoryId && !isLoading ? (
        <CategoryContent
          key={selectedCategoryId}
          categoryName={selectedCategoryName}
          selectedCategoryId={selectedCategoryId}
          listRef={listRef}
          translatedText={translatedText}
          sections={sections}
          isLoading={offersLoading}
          hidePills={hidePills}
          hasData={sections.length > 0}
          coords={location}
          onScroll={handleScroll}
          onContentSizeChange={handleContentSizeChange}
        />
      ) : null}

      <ScreenTransitionOverlay
        isLoading={isLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          hasError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={defaultTitle}
              messageBody={defaultMessage}
              primaryButton={{
                title: doneButtonTitle,
                onPress: () => router.back(),
              }}
              screenAnalyticsEventKey={translatedText('errorAnalyticsKey')}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default BrowseByCategoryOffers;
